/**
 * usePrototyperWorkbenchFacade
 *
 * Core streaming hook that connects AI SDK useChat to the @ideoz/workbench
 * stores. Manages WebContainer lifecycle, tool call dispatch, annotation
 * handling (brandLogo, projectCreated), and credits.
 *
 * Uses AI SDK 6+ (`ai` + `@ai-sdk/react`): `sendMessage`, `DefaultChatTransport`,
 * deferred `addToolOutput` (never sync inside `onToolCall`), `onData`, and `status`.
 */

import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useStore } from '@nanostores/react';
import type { WebContainer } from '@webcontainer/api';
import {
  bootWebContainer,
  getWebContainer,
  workbenchStore,
  executeWriteFile,
  persistUserFile,
  persistAssetFile,
  executeBuild,
  BuildAbortedError,
  executeConfigureSession,
  executeSearchReplace,
  executePrune,
  executeDeleteFile,
  strippedUnifiedDiff,
  hashFileContent,
  flushDirtyEditorFilesBeforeStream,
  recordProductionBuildOutcome,
  resetPersistedBuildCache,
  markSnapshotPreviewReady,
  restartDevServerAfterSnapshot,
  syncWorkbenchStoreWithWebContainerPersist,
  buildPrototypeSnapshotZipBlob,
  prototypeSnapshotContentFingerprint,
  waitForTemplateReady,
  applySnapshotZipToWebContainer,
  wcPerfAsync,
  HOT_EDIT_BRIDGE_REL_PATHS,
  getMergedFileContent,
  resetContainerToTemplate,
} from '@ideoz/workbench';
import { ApiUrls, prototyperService } from '@ideoz/core';
import { useAccessToken } from '@ideoz/core/hooks/useAccessToken';
import {
  isBenignMissingSnapshotError,
  isSupersededRestoreError,
} from './utils/snapshotRestoreErrors';
import {
  type AutoSnapshotReason,
  snapshotDedupKey,
  shouldDeferForBusy,
  shouldSkipForCooldown,
} from './utils/buildPhaseSnapshotGating';
import type { ResumeBuildOutcome } from './utils/resumeBuildGating';
import {
  lastToolStepBuildFailed,
  shouldAutoContinueAfterFailedBuild,
} from './utils/lastAssistantClientBuildFailedInUiMessages';
import { lastAssistantHasPendingClientTools } from './utils/lastAssistantHasPendingClientTools';
import { isServerExecutedPrototyperTool } from './utils/serverExecutedPrototyperTools';
import { scheduleStreamContinuation } from './utils/streamContinuation';
import {
  allowOpportunisticBuild,
  computeMaxToolContinuations,
  isEditTurnUnfinished,
  pathsTouchedThisTurn,
  plannedPathsFromPlanComplete,
} from './utils/editLoopCompleteness';
import {
  bumpTurnGeneration,
  createRestoreMutex,
  createTurnSession,
  isTurnStale,
  withRestoreMutex,
  abortRestore,
  type TurnSession,
  type RestoreMutex,
} from './utils/workbenchTurnSession';
import type { ToolStep } from './components/ToolCallStatusList';
import type { BuildTimelineV2 } from './utils/prototyperBuildPhases';
import {
  applyExploreEventToTimeline,
  applyPlanCompleteToTimeline,
  createEmptyTimeline,
  markStreamEndedWithoutSuccessfulBuild,
  markStreamError,
  markStreamSuccess,
  mergeLiveTerminalSegments,
  mergeTimelinePreservingAnnotations,
  timelineFromToolSteps,
} from './utils/prototyperBuildPhases';

/** Gate ConversationPageTemplate auto build-stream until S3 snapshot restore is decided. */
export type WorkbenchSnapshotBootstrap = 'pending' | 'restored' | 'none' | 'failed';

export interface UsePrototyperWorkbenchFacadeOptions {
  /**
   * When true, bootstrap WebContainer as soon as the conversation has a Nest project scope
   * (snapshot restore needs a ready container regardless of prototype panel visibility).
   */
  needsImmediateWorkbenchBoot?: boolean;
  /**
   * When true, prototype panel is open — allows deferred boot for tmp chats (no Nest project yet).
   */
  streamingWorkbenchPrototypePanelVisible?: boolean;
  conversationId?: string;
  /**
   * Ideoz project UUID. Prototype ZIP snapshots are Nest project-scoped; without this,
   * auto-upload is skipped (tmp chat persists edits only in WebContainer until a project exists).
   */
  projectId?: string;
  tmpConversationId?: string;
  modelProvider: string;
  modelChoice: string;
  onCreditsRefresh?: () => void;
  /**
   * Chef `projectCreated` annotation — `projectId` is the **Chef** id (build-stream body only).
   * Do not treat it as the Ideoz project UUID for `/projects/:id/...` APIs.
   */
  onProjectCreated?: (chefProjectId: string) => void;
  /** SSE `demoLinkCreated` — persist `prototyper_demo_link` id for legacy Publish UI */
  onStreamDemoLinkId?: (id: number) => void;
  /**
   * Flask `stream_prototype_build` uses this id to wait for/select the persisted prototyper interaction.
   * Set immediately before triggering `startGeneration` (see ConversationPageTemplate → ConversationPage).
   */
  streamingBuildLlmInteractionIdRef?: MutableRefObject<string | undefined>;
  /** Set when a build-stream starts so sessionStorage / chat id are scoped per turn. */
  streamingLlmInteractionId?: string;
  /** Short label for prototyper_build_sessions (prior user message). */
  getPrototypeDisplayName?: () => string | undefined;
  /** Patch Redux assistant row (e.g. demo link id, success banner) by backend llm interaction id. */
  onPatchMessageByLlmInteractionId?: (llmInteractionId: string, patch: Record<string, unknown>) => void;
  /** Called when a build-stream turn completes with a successful production build. */
  onBuildStreamTurnCompleted?: (llmInteractionId: string) => void;
  /** Called after boot-time S3 snapshot restore finishes. */
  onSnapshotRestored?: (meta: {
    llmInteractionId?: string | null;
    buildVerified?: boolean;
  }) => void;
}

export type PrototypeSnapshotRestoreHint = {
  buildStatus?: string;
  hasBuildTimeline?: boolean;
};

const WEBCONTAINER_BOOT_WAIT_MS = 45_000;

async function waitForWebContainerReady(
  getWc: () => WebContainer | null | undefined,
  timeoutMs = WEBCONTAINER_BOOT_WAIT_MS,
): Promise<WebContainer | null> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const status = workbenchStore.bootStatus.get();
    if (status === 'error') {
      return null;
    }
    const wc = getWc();
    if (wc && status === 'ready') {
      return wc;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 150));
  }
  return getWc() ?? null;
}

function getToolInput(toolCall: { input?: unknown }): Record<string, unknown> {
  const input = toolCall.input;
  return input && typeof input === 'object' && input !== null && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : {};
}

const WORKBENCH_MANUAL_SKIP_FRAGMENTS = ['/node_modules/', '/.git/'] as const;
const WORKBENCH_MANUAL_MAX_TOTAL_CHARS = 380_000;
const WORKBENCH_MANUAL_MAX_FILE_CHARS = 60_000;
const WORKBENCH_MANUAL_MAX_FILES = 40;

const WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS = 12_800;
const WORKBENCH_MANUAL_DIFF_MAX_TOTAL_CHARS = 100_000;

function workbenchManualPathDisallowed(filePath: string): boolean {
  const p = filePath.replace(/\\/g, '/').replace(/^\/+/, '');
  return WORKBENCH_MANUAL_SKIP_FRAGMENTS.some((frag) => p.includes(frag));
}

function isHotEditBridgePath(filePath: string): boolean {
  return (HOT_EDIT_BRIDGE_REL_PATHS as readonly string[]).includes(filePath);
}

function collectWorkbenchManualFilesForRequest(): Record<string, string> | undefined {
  const dirty = workbenchStore.getModifiedFiles();
  const dirtyPaths = Object.keys(dirty).filter((p) => p && !workbenchManualPathDisallowed(p));
  if (dirtyPaths.length === 0) {
    workbenchStore.manualContextTruncationWarning.set(null);
    return undefined;
  }

  const bridgeExtras = HOT_EDIT_BRIDGE_REL_PATHS.filter((p) => !dirtyPaths.includes(p));
  const mergedPaths = [...bridgeExtras, ...dirtyPaths.sort()];
  const cappedPaths = mergedPaths.slice(0, WORKBENCH_MANUAL_MAX_FILES);
  const out: Record<string, string> = {};
  let total = 0;
  let truncatedByFiles = mergedPaths.length > WORKBENCH_MANUAL_MAX_FILES;
  let truncatedByChars = false;
  let perFileTruncated = false;

  for (const path of cappedPaths) {
    let body = dirty[path];
    if (body === undefined && isHotEditBridgePath(path)) {
      body = getMergedFileContent(path);
    }
    if (body === undefined) body = '';
    const originalLen = body.length;
    if (body.length > WORKBENCH_MANUAL_MAX_FILE_CHARS) {
      body = `${body.slice(0, WORKBENCH_MANUAL_MAX_FILE_CHARS)}\n\n...[truncated]\n\n`;
      perFileTruncated = true;
    }
    if (total + body.length > WORKBENCH_MANUAL_MAX_TOTAL_CHARS) {
      truncatedByChars = true;
      break;
    }
    out[path] = body;
    total += body.length;
    if (originalLen > WORKBENCH_MANUAL_MAX_FILE_CHARS) {
      perFileTruncated = true;
    }
  }

  if (Object.keys(out).length < dirtyPaths.length) {
    truncatedByFiles = true;
  }

  updateManualContextTruncationWarning(dirtyPaths.length, Object.keys(out).length, {
    truncatedByFiles,
    truncatedByChars,
    perFileTruncated,
  });

  return Object.keys(out).length > 0 ? out : undefined;
}

function updateManualContextTruncationWarning(
  dirtyCount: number,
  includedCount: number,
  flags: { truncatedByFiles: boolean; truncatedByChars: boolean; perFileTruncated: boolean },
): void {
  const parts: string[] = [];
  if (flags.truncatedByFiles && dirtyCount > includedCount) {
    parts.push(`only ${includedCount} of ${dirtyCount} edited files were sent to the AI`);
  } else if (flags.truncatedByFiles) {
    parts.push(`manual edit context capped at ${WORKBENCH_MANUAL_MAX_FILES} files`);
  }
  if (flags.truncatedByChars) {
    parts.push('total manual-edit payload hit the size limit');
  }
  if (flags.perFileTruncated) {
    parts.push('some file bodies were truncated');
  }
  if (parts.length === 0) {
    workbenchStore.manualContextTruncationWarning.set(null);
    return;
  }
  workbenchStore.manualContextTruncationWarning.set(
    `Manual edit context was capped: ${parts.join('; ')}. The AI may not see all of your changes.`,
  );
}

function collectWorkbenchManualFileHashesForRequest(
  manualFiles?: Record<string, string>,
): Record<string, string> | undefined {
  if (!manualFiles || Object.keys(manualFiles).length === 0) return undefined;
  const dirty = workbenchStore.getModifiedFiles();
  const out: Record<string, string> = {};
  for (const path of Object.keys(manualFiles).sort()) {
    const body = manualFiles[path] ?? '';
    out[path] = hashFileContent(body);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function collectWorkbenchManualFileDiffsForRequest(): Record<string, string> | undefined {
  const dirty = workbenchStore.getModifiedFiles();
  const paths = Object.keys(dirty).filter((p) => p && !workbenchManualPathDisallowed(p));
  if (paths.length === 0) return undefined;
  paths.sort();
  const cappedPaths = paths.slice(0, WORKBENCH_MANUAL_MAX_FILES);
  const out: Record<string, string> = {};
  let total = 0;
  for (const path of cappedPaths) {
    const baseline = workbenchStore.getLastToolWritten(path);
    if (baseline === undefined) continue;
    const curr = dirty[path] ?? '';
    const diffSnippet = strippedUnifiedDiff(path, baseline, curr);
    if (!diffSnippet) continue;
    let clipped = diffSnippet;
    if (clipped.length > WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS) {
      clipped = `${clipped.slice(0, WORKBENCH_MANUAL_DIFF_MAX_FILE_CHARS)}\n\n...[diff truncated]\n\n`;
    }
    if (total + clipped.length > WORKBENCH_MANUAL_DIFF_MAX_TOTAL_CHARS) {
      break;
    }
    out[path] = clipped;
    total += clipped.length;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function workbenchBrowserPersistKey(chatId: string): string {
  return `ideoz:wbt:v1:${chatId}`;
}

/** Debounce POST/PATCH build-session while tool steps stream. */
const BUILD_SESSION_PATCH_DEBOUNCE_MS = 450;
/** Debounce after file edits before uploading a ZIP snapshot. */
const AUTO_SNAPSHOT_FILE_DEBOUNCE_MS = 4_000;
/** Min gap between *file-edit* snapshots (separate from build/stream cooldown). */
const AUTO_SNAPSHOT_COOLDOWN_FILES_MS = 5_000;
/** Min gap after a build- or stream-end snapshot (kept short; separate from file cooldown). */
const AUTO_SNAPSHOT_COOLDOWN_BUILD_STREAM_MS = 2_000;
/** How long a forced snapshot waits out `restoreInProgress`'s trailing flag before giving up. */
const SNAPSHOT_RESTORE_WAIT_MS = 8_000;
/** How long a forced snapshot waits behind an already-in-flight upload before proceeding anyway. */
const SNAPSHOT_CHAIN_MAX_WAIT_MS = 30_000;
/** Bound on a locally-resumed build before it's treated as stalled. */
const RESUME_BUILD_MAX_MS = 240_000;
const MAX_BUILD_REPEAT_ERRORS = 3;
const MAX_TRUNCATION_CONTINUES = 3;
const MAX_RETAINED_TOOL_STEPS = 200;
const TOOL_STEPS_PERSIST_DEBOUNCE_MS = 300;

function capToolSteps(steps: ToolStep[]): ToolStep[] {
  if (steps.length <= MAX_RETAINED_TOOL_STEPS) return steps;
  return steps.slice(steps.length - MAX_RETAINED_TOOL_STEPS);
}

export function usePrototyperWorkbenchFacade(options: UsePrototyperWorkbenchFacadeOptions) {
  const {
    conversationId,
    projectId,
    tmpConversationId,
    modelProvider,
    modelChoice,
    onCreditsRefresh,
    onProjectCreated,
    onStreamDemoLinkId,
    streamingBuildLlmInteractionIdRef,
    streamingLlmInteractionId,
    getPrototypeDisplayName,
    onPatchMessageByLlmInteractionId,
    onBuildStreamTurnCompleted,
    onSnapshotRestored,
    needsImmediateWorkbenchBoot = false,
    streamingWorkbenchPrototypePanelVisible = false,
  } = options;

  const accessToken = useAccessToken();
  const webcontainerRef = useRef<WebContainer | null>(null);
  const processedAnnotationsRef = useRef(new Set<string>());
  const writtenAssetPathsRef = useRef(new Set<string>());
  const assetWriteChainRef = useRef(Promise.resolve());
  const [buildTimeline, setBuildTimeline] = useState<BuildTimelineV2 | null>(null);
  const buildTimelineRef = useRef<BuildTimelineV2 | null>(null);
  /** Annotation overlay (planComplete / postBuild) — preserved when toolSteps rebuild timeline. */
  const timelineAnnotationRef = useRef<BuildTimelineV2 | null>(null);
  const buildSessionPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buildSessionPrototypeMetaRef = useRef<{
    prototypeVersionIndex?: number | null;
    prototypeDisplayName?: string | null;
  }>({});
  const [toolSteps, setToolSteps] = useState<ToolStep[]>([]);
  const toolStepsRef = useRef<ToolStep[]>([]);
  /** Bumps when `chefProjectIdRef` changes without `toolSteps`, so sessionStorage stays in sync. */
  const [persistVersion, setPersistVersion] = useState(0);
  const [snapshotBootstrap, setSnapshotBootstrap] = useState<WorkbenchSnapshotBootstrap>('pending');
  const [snapshotRestoreError, setSnapshotRestoreError] = useState<string | null>(null);
  const [warmBootLatch, setWarmBootLatch] = useState(0);
  const bootStatus = useStore(workbenchStore.bootStatus);
  const buildStatus = useStore(workbenchStore.buildStatus);

  const shouldBootWebContainer =
    needsImmediateWorkbenchBoot ||
    streamingWorkbenchPrototypePanelVisible ||
    warmBootLatch > 0;

  const accessTokenRef = useRef(accessToken);
  const modelRef = useRef({ modelProvider, modelChoice });
  const chefProjectIdRef = useRef<string | null>(null);
  const buildRepeatRef = useRef({ lastSig: '', count: 0 });
  const lastFileSnapshotAtRef = useRef(0);
  const lastBuildStreamSnapshotAtRef = useRef(0);
  const autoSnapshotInFlightRef = useRef(false);
  const autoSnapshotChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const resumeBuildInFlightRef = useRef<string | null>(null);
  const resumeBuildAttemptedRef = useRef<Set<string>>(new Set());
  const resumeBuildAbortRef = useRef<AbortController | null>(null);
  const projectConversationRef = useRef({
    projectId: projectId as string | undefined,
    conversationId: conversationId as string | undefined,
  });
  const fileSnapshotDebounceRef = useRef<number | undefined>(undefined);
  const pendingFileSnapshotAfterBusyRef = useRef(false);
  const fileSnapshotCooldownRetryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const lastSnapshotFingerprintByInteractionRef = useRef<Map<string, string>>(new Map());
  const bootstrapAttemptedKeyRef = useRef<string | undefined>(undefined);

  type AddToolOutputArgs =
    | { tool: string; toolCallId: string; state?: 'output-available'; output: unknown }
    | { tool: string; toolCallId: string; state: 'output-error'; errorText: string };

  const addToolOutputRef = useRef<
    ((args: AddToolOutputArgs) => void | PromiseLike<void>) | null
  >(null);

  /** Latest `sendMessage` for `onFinish` / post-tool continuation (SDK callbacks run via ref). */
  const sendMessageRef = useRef<((msg?: unknown) => void) | null>(null);
  const messagesRef = useRef<readonly unknown[]>([]);
  const continueScheduleGenRef = useRef(0);
  /** Cancels an in-flight continuation scheduler when a new one starts. */
  const cancelStreamContinuationRef = useRef<(() => void) | null>(null);
  /** Avoid duplicate POSTs when onFinish and post-tool callbacks race. */
  const continuationSentRef = useRef(false);
  /** Client tool output must be POSTed once; cleared after send or successful turn end. */
  const toolResultContinuationPendingRef = useRef(false);
  /** Latest useChat `status` — continuation must not POST while assistant stream is still open. */
  const chatStatusRef = useRef<string>('ready');
  /** Last turn ended on `finishReason: "length"` (reported by Chef's usage annotation). */
  const streamTruncatedRef = useRef(false);
  /** Bounds how many times a truncated turn may be auto-resumed. */
  const truncationContinueRef = useRef(0);
  /** Bounds how many build-stream POST continuations one turn may trigger. */
  const toolContinuationCountRef = useRef(0);
  /** planComplete.files paths for the active edit turn (drives completeness + continuation cap). */
  const editPlannedFilePathsRef = useRef<string[]>([]);

  const turnSessionRef = useRef<TurnSession>(createTurnSession());
  /**
   * Mirrors `turnSessionRef`'s aborted state as a real `AbortSignal` so tool-adapter
   * calls (`executeWriteFile`/`executeBuild`/…) can bail out of their own in-flight
   * `wc.fs.writeFile`/`wc.spawn` calls instead of only having their *output* suppressed
   * after the fact — closes the window where a stale turn's write/build still lands on
   * the shared WebContainer after the user has switched to a different conversation.
   */
  const turnAbortControllerRef = useRef<AbortController>(new AbortController());
  const abortCurrentTurn = useCallback((): void => {
    bumpTurnGeneration(turnSessionRef.current, true);
    turnAbortControllerRef.current.abort();
    turnAbortControllerRef.current = new AbortController();
  }, []);
  const deferToolOutputTimeoutRefs = useRef<number[]>([]);
  const restoreMutexRef = useRef<RestoreMutex>(createRestoreMutex());
  const resetConversationKeyRef = useRef<string | undefined>(undefined);
  const toolTurnGenerationRef = useRef<number>(0);
  const clientToolsInFlightRef = useRef(0);
  const suppressStreamAbortRef = useRef(false);

  const [activeSnapshotInteractionId, setActiveSnapshotInteractionId] = useState<string | null>(
    null,
  );
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const activeSnapshotInteractionIdRef = useRef<string | null>(null);
  const activeSnapshotIdRef = useRef<string | null>(null);
  const [restoreInteractionLoadingId, setRestoreInteractionLoadingId] = useState<string | null>(
    null,
  );
  const [workbenchPersistError, setWorkbenchPersistError] = useState<string | null>(null);
  const [snapshotIndexLoading, setSnapshotIndexLoading] = useState(false);
  const [bootstrapRetryNonce, setBootstrapRetryNonce] = useState(0);
  const snapshotIndexRef = useRef<Map<string, import('./utils/shouldStartBuildStream').SnapshotIndexEntry>>(
    new Map(),
  );
  const toolStepsPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timelineRebuildRafRef = useRef<number | null>(null);

  const clearDeferToolOutputTimeouts = useCallback((): void => {
    for (const id of deferToolOutputTimeoutRefs.current) {
      window.clearTimeout(id);
    }
    deferToolOutputTimeoutRefs.current = [];
  }, []);

  const resetWorkbenchTurnState = useCallback((): void => {
    abortCurrentTurn();
    resumeBuildAbortRef.current?.abort();
    resumeBuildAbortRef.current = null;
    resumeBuildInFlightRef.current = null;
    resumeBuildAttemptedRef.current = new Set();
    clearDeferToolOutputTimeouts();
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = null;
    continuationSentRef.current = false;
    toolResultContinuationPendingRef.current = false;
    buildRepeatRef.current = { lastSig: '', count: 0 };
    toolContinuationCountRef.current = 0;
    editPlannedFilePathsRef.current = [];
    truncationContinueRef.current = 0;
    streamTruncatedRef.current = false;
    processedAnnotationsRef.current.clear();
    writtenAssetPathsRef.current.clear();
    assetWriteChainRef.current = Promise.resolve();
    timelineAnnotationRef.current = null;
    buildTimelineRef.current = null;
    setBuildTimeline(null);
    setToolSteps([]);
    toolStepsRef.current = [];
    setActiveSnapshotInteractionId(null);
    setActiveSnapshotId(null);
    abortRestore(restoreMutexRef.current);
  }, [clearDeferToolOutputTimeouts, abortCurrentTurn]);

  /**
   * Never call `addToolOutput` synchronously inside `onToolCall` — the AI SDK runs
   * `onToolCall` on a SerialJobExecutor and `addToolOutput` deadlocks (never settles).
   */
  const deferAddToolOutput = useCallback((payload: AddToolOutputArgs, capturedTurn: number): void => {
    const add = addToolOutputRef.current;
    if (!add) return;
    const timeoutId = window.setTimeout(() => {
      deferToolOutputTimeoutRefs.current = deferToolOutputTimeoutRefs.current.filter(
        (id) => id !== timeoutId,
      );
      if (isTurnStale(turnSessionRef.current, capturedTurn)) {
        return;
      }
      void Promise.resolve(add(payload))
        .then(() => {
          if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
          scheduleContinueAfterClientTools();
        })
        .catch((err: unknown) => {
          console.warn('[Workbench] addToolOutput failed', err);
          setWorkbenchPersistError('Tool output could not be sent. Try Retry or refresh.');
        });
    }, 0);
    deferToolOutputTimeoutRefs.current.push(timeoutId);
  }, []);

  const getEditLoopCompletenessInput = useCallback(
    (messages: readonly unknown[]) => ({
      messages,
      toolSteps: toolStepsRef.current,
      plannedFilePaths: editPlannedFilePathsRef.current,
      toolResultContinuationPending: toolResultContinuationPendingRef.current,
      clientToolsBusy: workbenchStore.clientToolsBusy.get(),
    }),
    [],
  );

  const requestStreamContinuation = useCallback(() => {
    if (turnSessionRef.current.aborted) return;
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = scheduleStreamContinuation({
      getMessages: () => messagesRef.current,
      getToolSteps: () => toolStepsRef.current,
      getChatStatus: () => chatStatusRef.current,
      getBuildRepeatCount: () => buildRepeatRef.current.count,
      getToolResultContinuationPending: () => toolResultContinuationPendingRef.current,
      getToolContinuationCount: () => toolContinuationCountRef.current,
      getPlannedFilePaths: () => editPlannedFilePathsRef.current,
      getTruncated: () => streamTruncatedRef.current,
      getClientToolsBusy: () => workbenchStore.clientToolsBusy.get(),
      maxBuildRepeatErrors: MAX_BUILD_REPEAT_ERRORS,
      maxToolContinuations: () =>
        computeMaxToolContinuations(
          Math.max(
            editPlannedFilePathsRef.current.length,
            pathsTouchedThisTurn(toolStepsRef.current).size,
          ),
        ),
      onStreaming: (streaming) => {
        workbenchStore.isStreaming.set(streaming);
      },
      onExhausted: (reason) => {
        workbenchStore.isStreaming.set(false);
        const base = buildTimelineRef.current ?? createEmptyTimeline();
        const finalT = markStreamEndedWithoutSuccessfulBuild(base);
        buildTimelineRef.current = finalT;
        setBuildTimeline(finalT);
      },
      send: () => {
        if (turnSessionRef.current.aborted) return;
        if (continuationSentRef.current) return;
        continuationSentRef.current = true;
        toolContinuationCountRef.current += 1;
        toolResultContinuationPendingRef.current = false;
        window.setTimeout(() => {
          continuationSentRef.current = false;
        }, 500);
        void sendMessageRef.current?.();
      },
    });
  }, []);

  const scheduleContinueAfterClientTools = useCallback(() => {
    ++continueScheduleGenRef.current;
    requestStreamContinuation();
  }, [requestStreamContinuation]);

  const tryAutoContinueAfterFailedBuild = useCallback(
    (finishMessages: readonly unknown[]): boolean => {
      if (buildRepeatRef.current.count >= MAX_BUILD_REPEAT_ERRORS) return false;
      if (!shouldAutoContinueAfterFailedBuild(finishMessages, toolStepsRef.current)) {
        return false;
      }
      requestStreamContinuation();
      return true;
    },
    [requestStreamContinuation],
  );

  /**
   * A turn cut off at `maxOutputTokens` stops before the model emits `build`, so the
   * prototype never gets compiled. Resume it so the model can finish and call `build`.
   */
  const tryAutoContinueAfterTruncation = useCallback((): boolean => {
    if (!streamTruncatedRef.current) return false;
    if (truncationContinueRef.current >= MAX_TRUNCATION_CONTINUES) return false;
    truncationContinueRef.current += 1;
    requestStreamContinuation();
    return true;
  }, [requestStreamContinuation]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    modelRef.current = { modelProvider, modelChoice };
  }, [modelProvider, modelChoice]);

  useEffect(() => {
    activeSnapshotInteractionIdRef.current = activeSnapshotInteractionId;
    activeSnapshotIdRef.current = activeSnapshotId;
  }, [activeSnapshotInteractionId, activeSnapshotId]);

  useEffect(() => {
    projectConversationRef.current = { projectId, conversationId };
  }, [projectId, conversationId]);

  const patchBuildSessionApi = useCallback(
    async (body: Record<string, unknown>) => {
      const id = streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId;
      if (!id) return;
      try {
        await prototyperService.patchPrototyperBuildSession(id, body);
      } catch (e) {
        console.warn('[Workbench] prototyper build session PATCH failed', e);
      }
    },
    [streamingLlmInteractionId],
  );

  const recordSnapshotFingerprintForInteraction = useCallback(
    (llmInteractionId: string, buildVerified: boolean): void => {
      lastSnapshotFingerprintByInteractionRef.current.set(
        snapshotDedupKey(llmInteractionId, buildVerified),
        prototypeSnapshotContentFingerprint(),
      );
    },
    [],
  );

  const runAutoPrototypeSnapshot = useCallback(
    async (
      reason: AutoSnapshotReason,
      options?: {
        buildVerified?: boolean;
        retry?: boolean;
        /** Bypass the streaming/busy + cooldown gates. Build-phase callers only. */
        force?: boolean;
        /** Explicit attribution for turns whose `activeSnapshotInteractionId` isn't set yet. */
        llmInteractionId?: string;
      },
    ) => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!cid) return;
      if (!pid) {
        return;
      }
      const force = options?.force === true;
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return;
      if (workbenchStore.bootStatus.get() !== 'ready') return;
      if (workbenchStore.restoreInProgress.get()) {
        // `applySnapshotZipToWebContainer` holds this flag for ~4.5s *after* its writes land.
        // Hard-returning here would silently drop the verified upload of a fast resumed build,
        // so a forced caller waits it out instead of giving up.
        if (!force) return;
        const waitUntil = Date.now() + SNAPSHOT_RESTORE_WAIT_MS;
        while (workbenchStore.restoreInProgress.get() && Date.now() < waitUntil) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
        }
      }

      if (
        shouldDeferForBusy({
          force,
          isStreaming: workbenchStore.isStreaming.get(),
          clientToolsBusy: workbenchStore.clientToolsBusy.get(),
        })
      ) {
        if (reason === 'files') {
          pendingFileSnapshotAfterBusyRef.current = true;
        }
        return;
      }

      const streamingTurnId =
        streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId ?? null;
      const activeTurnId = activeSnapshotInteractionId;
      const attributionId =
        options?.llmInteractionId ??
        (reason === 'files' ? activeTurnId : streamingTurnId ?? activeTurnId);

      if (!attributionId) return;

      if (
        reason === 'files' &&
        streamingTurnId &&
        activeTurnId &&
        streamingTurnId !== activeTurnId
      ) {
        return;
      }

      if (autoSnapshotInFlightRef.current) {
        if (!force) {
          if (reason === 'files') {
            pendingFileSnapshotAfterBusyRef.current = true;
          }
          return;
        }
        // A forced call (build-phase) must not be dropped just because another upload is
        // mid-flight — e.g. the pre-build snapshot may still be uploading when the build
        // finishes. Queue behind it instead, bounded so a wedged upload can't wedge this one.
        await Promise.race([
          autoSnapshotChainRef.current.catch(() => undefined),
          new Promise((resolve) => window.setTimeout(resolve, SNAPSHOT_CHAIN_MAX_WAIT_MS)),
        ]);
      }

      const now = Date.now();
      const sinceFile = now - lastFileSnapshotAtRef.current;
      const sinceBuildStream = now - lastBuildStreamSnapshotAtRef.current;

      if (
        shouldSkipForCooldown({
          reason,
          force,
          sinceFileMs: sinceFile,
          sinceBuildStreamMs: sinceBuildStream,
          fileCooldownMs: AUTO_SNAPSHOT_COOLDOWN_FILES_MS,
          buildStreamCooldownMs: AUTO_SNAPSHOT_COOLDOWN_BUILD_STREAM_MS,
        })
      ) {
        if (reason === 'files') {
          const wait = AUTO_SNAPSHOT_COOLDOWN_FILES_MS - sinceFile + 200;
          if (fileSnapshotCooldownRetryTimerRef.current !== undefined) {
            clearTimeout(fileSnapshotCooldownRetryTimerRef.current);
          }
          fileSnapshotCooldownRetryTimerRef.current = setTimeout(() => {
            fileSnapshotCooldownRetryTimerRef.current = undefined;
            void runAutoPrototypeSnapshot('files');
          }, wait);
        }
        return;
      }

      const wantVerified = options?.buildVerified === true;
      const fingerprint = prototypeSnapshotContentFingerprint();
      const priorFingerprint = lastSnapshotFingerprintByInteractionRef.current.get(
        snapshotDedupKey(attributionId, wantVerified),
      );
      if (priorFingerprint === fingerprint) {
        if (reason === 'stream-end') {
          onBuildStreamTurnCompleted?.(attributionId);
        }
        // Content hasn't changed since the last upload for this exact (interaction,
        // buildVerified) pair — skip re-uploading, but still hand the caller the snapshot
        // that's already on the server instead of `undefined`. `uploadVerifiedPrototypeSnapshot`
        // otherwise has to fall back to `activeSnapshotId`, which is empty the first time
        // publish is invoked in a session that restored via bootstrap (no live upload ever
        // ran), even though a verified snapshot already exists at this fingerprint.
        const existing = snapshotIndexRef.current.get(attributionId);
        if (existing) {
          return { snapshotId: existing.snapshotId, revision: existing.revision, status: 'ready' };
        }
        return undefined;
      }

      autoSnapshotInFlightRef.current = true;
      const uploadPromise = (async () => {
        try {
          const lid = attributionId;
          const blob = await buildPrototypeSnapshotZipBlob();
          const finalized = await prototyperService.uploadPrototypeSnapshotZip(
            pid,
            cid,
            blob,
            lid,
            options?.buildVerified,
          );
          const at = Date.now();
          if (reason === 'files') {
            lastFileSnapshotAtRef.current = at;
          } else {
            lastBuildStreamSnapshotAtRef.current = at;
          }
          if (finalized) {
            recordSnapshotFingerprintForInteraction(lid, wantVerified);
            setActiveSnapshotId(finalized.snapshotId);
            const prevEntry = snapshotIndexRef.current.get(lid);
            snapshotIndexRef.current.set(lid, {
              llmInteractionId: lid,
              snapshotId: finalized.snapshotId,
              revision: finalized.revision,
              createdAt: new Date().toISOString(),
              ...(wantVerified || prevEntry?.buildVerified ? { buildVerified: true } : {}),
            });
            if (lid === streamingTurnId) {
              await patchBuildSessionApi({
                prototypeSnapshotId: finalized.snapshotId,
              });
            }
            if (reason === 'stream-end') {
              onBuildStreamTurnCompleted?.(lid);
            }
          }
          setWorkbenchPersistError(null);
          return finalized;
        } catch (e) {
          console.warn('[Workbench] Auto prototype snapshot failed', { reason, err: e });
          if (reason !== 'files' && !options?.retry) {
            setTimeout(() => {
              void runAutoPrototypeSnapshot(reason, { ...options, retry: true });
            }, 2000);
          } else {
            setWorkbenchPersistError('Could not save prototype snapshot. Changes may not persist.');
          }
          return undefined;
        } finally {
          autoSnapshotInFlightRef.current = false;
          if (pendingFileSnapshotAfterBusyRef.current) {
            pendingFileSnapshotAfterBusyRef.current = false;
            queueMicrotask(() => {
              void runAutoPrototypeSnapshot('files');
            });
          }
        }
      })();
      autoSnapshotChainRef.current = uploadPromise;
      return uploadPromise;
    },
    [
      patchBuildSessionApi,
      streamingLlmInteractionId,
      activeSnapshotInteractionId,
      onBuildStreamTurnCompleted,
      recordSnapshotFingerprintForInteraction,
    ],
  );

  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const clearDebounce = (): void => {
      if (fileSnapshotDebounceRef.current !== undefined) {
        clearTimeout(fileSnapshotDebounceRef.current);
        fileSnapshotDebounceRef.current = undefined;
      }
    };

    const unsub = workbenchStore.files.subscribe(() => {
      if (workbenchStore.bootStatus.get() !== 'ready') return;
      if (workbenchStore.restoreInProgress.get()) return;
      clearDebounce();
      fileSnapshotDebounceRef.current = window.setTimeout(() => {
        fileSnapshotDebounceRef.current = undefined;
        void runAutoPrototypeSnapshot('files');
      }, AUTO_SNAPSHOT_FILE_DEBOUNCE_MS);
    });

    return () => {
      unsub();
      clearDebounce();
      if (fileSnapshotCooldownRetryTimerRef.current !== undefined) {
        clearTimeout(fileSnapshotCooldownRetryTimerRef.current);
        fileSnapshotCooldownRetryTimerRef.current = undefined;
      }
    };
  }, [projectId, conversationId, runAutoPrototypeSnapshot]);

  useEffect(() => {
    const conversationKey = `${conversationId ?? ''}:${tmpConversationId ?? ''}`;
    if (resetConversationKeyRef.current === undefined) {
      resetConversationKeyRef.current = conversationKey;
      return;
    }
    if (resetConversationKeyRef.current === conversationKey) return;
    resetConversationKeyRef.current = conversationKey;
    setWarmBootLatch(0);
    resetWorkbenchTurnState();
    abortRestore(restoreMutexRef.current);
    snapshotIndexRef.current = new Map();
    setSnapshotIndexLoading(false);
    // Must run before the WebContainer boot/link effect below (declared later, so it
    // cleans up + re-runs after this effect within the same commit): that effect's
    // `syncWorkbenchStoreWithWebContainerPersist()` blindly re-applies the last known
    // build status / preview URL on every remount, including one caused by switching
    // to a different conversation. Clearing the cache here — before that resync can
    // read it — keeps a finished chat's "build succeeded" state from flashing onto a
    // different, never-built chat right after the switch.
    resetPersistedBuildCache();
    workbenchStore.previewReady.set(false);
    lastSnapshotFingerprintByInteractionRef.current = new Map();
    bootstrapAttemptedKeyRef.current = undefined;
  }, [conversationId, tmpConversationId, resetWorkbenchTurnState]);

  useEffect(() => {
    // Only relevant once the workbench is actually going to be used — the index it builds
    // (`snapshotIndexRef`) is consumed solely by `shouldStartBuildStream`, which only ever
    // runs once a prototyper build-stream might start. Without this gate, opening *any*
    // conversation — including a pure Clarify one that has never touched Prototyper — fires
    // `GET .../prototype/snapshots` for it, purely wasted network noise for the common case.
    if (!projectId || !conversationId || !shouldBootWebContainer) {
      setSnapshotIndexLoading(false);
      return;
    }
    let cancelled = false;
    const abortController = new AbortController();
    const timeoutId = window.setTimeout(() => {
      abortController.abort();
    }, 4_000);
    setSnapshotIndexLoading(true);
    void (async () => {
      try {
        const getIndex = prototyperService.getPrototypeSnapshotIndex?.bind(prototyperService);
        if (typeof getIndex !== 'function') {
          throw new Error('getPrototypeSnapshotIndex unavailable');
        }
        const entries = await getIndex(projectId, conversationId);
        if (cancelled || abortController.signal.aborted) return;
        snapshotIndexRef.current = new Map(
          entries.map((e) => [e.llmInteractionId, e]),
        );
      } catch {
        if (!cancelled) snapshotIndexRef.current = new Map();
      } finally {
        window.clearTimeout(timeoutId);
        if (!cancelled) setSnapshotIndexLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      abortController.abort();
      window.clearTimeout(timeoutId);
    };
  }, [projectId, conversationId, shouldBootWebContainer]);

  /**
   * After refresh: restore the most recent verified snapshot into the WebContainer.
   * ConversationPageTemplate must not auto-call build-stream when bootstrap === 'restored'.
   *
   * "Latest" is derived from the snapshot index (already fetched above, whenever the
   * workbench boots) rather than a dedicated `/prototype/snapshot/latest` call — the index
   * already carries `createdAt` + `buildVerified` per interaction, and `getPrototypeSnapshotByInteraction`
   * (already used by the explicit generation-pill restore) returns the same shape the actual
   * download needs. This also means a brand-new conversation with an empty index — the common
   * case when a new chat enters Prototyper mode — skips straight to 'none' with zero network
   * calls, instead of firing a request that's guaranteed to 404.
   */
  useEffect(() => {
    if (!projectId || !conversationId) {
      setSnapshotBootstrap('none');
      return;
    }
    if (typeof window === 'undefined' || !window.crossOriginIsolated) {
      setSnapshotBootstrap('none');
      return;
    }
    if (bootStatus === 'error') {
      setSnapshotBootstrap('none');
      return;
    }
    if (bootStatus !== 'ready' || snapshotIndexLoading) {
      setSnapshotBootstrap('pending');
      return;
    }

    const indexEntries = Array.from(snapshotIndexRef.current.values());
    if (indexEntries.length === 0) {
      setSnapshotBootstrap('none');
      return;
    }
    const byNewest = [...indexEntries].sort(
      (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
    );
    const latestEntry = byNewest.find((e) => e.buildVerified === true) ?? byNewest[0];

    const bootstrapKey = `${conversationId}:${bootstrapRetryNonce}`;
    // Order matters, and it used to be wrong. This effect re-runs whenever `bootStatus` dips out
    // of 'ready' (which `workbenchStore.reset()` does on every boot-effect cleanup) or
    // `snapshotIndexLoading` flips true again — and each of those re-runs writes 'pending' at the
    // `bootStatus !== 'ready'` guard above. So every early-return below has to leave the state
    // machine somewhere terminal; a bare `return` re-pins 'pending' forever, which keeps
    // `workbenchPreparing` (ConversationPage) true and unmounts the whole workbench subtree —
    // including every error/retry affordance that could rescue it.
    if (activeSnapshotInteractionIdRef.current) {
      // A snapshot is already applied to the container. Must be checked *before* the key guard:
      // once `bootstrapAttemptedKeyRef` matches, the old ordering made this branch unreachable,
      // so a restored session that later saw a `bootStatus` dip could never get back out of
      // 'pending'.
      setSnapshotBootstrap('restored');
      return;
    }
    if (restoreMutexRef.current.abortController !== null) {
      // A restore genuinely owns the outcome right now (this bootstrap's, or an explicit
      // generation-pill click that superseded it) — 'pending' is the honest state and that
      // owner settles it.
      return;
    }
    if (bootstrapAttemptedKeyRef.current === bootstrapKey) {
      // Already attempted for this conversation+nonce, nothing in flight, and no snapshot
      // landed — the attempt is over, so don't leave 'pending' pinned. Preserve a terminal
      // value a previous run already reached ('failed' keeps the retry UI reachable).
      setSnapshotBootstrap((prev) => (prev === 'pending' ? 'none' : prev));
      return;
    }

    bootstrapAttemptedKeyRef.current = bootstrapKey;
    setSnapshotRestoreError(null);
    setSnapshotBootstrap('pending');

    /**
     * Routed through the same `RestoreMutex` as `restorePrototypeSnapshotForInteraction` so
     * this automatic restore and an explicit generation-pill click can't race each other —
     * whichever starts second aborts the other via `signal`, and `applySnapshotZipToWebContainer`
     * genuinely honors that signal mid-write (unlike the plain `cancelled` boolean this replaced,
     * which only gated whether the *result* got applied, not whether the writes themselves kept
     * running after being superseded).
     */
    void withRestoreMutex(restoreMutexRef.current, `bootstrap:${conversationId}`, async (signal) => {
      try {
        let latestMeta: {
          llmInteractionId?: string | null;
          buildVerified?: boolean;
          snapshotId?: string;
        } = {};
        const blob = await wcPerfAsync('wc:snapshot:downloadZip', async () => {
          const latest = await prototyperService.getPrototypeSnapshotByInteraction(
            projectId,
            conversationId,
            latestEntry.llmInteractionId,
          );
          latestMeta = {
            llmInteractionId: latest.llmInteractionId,
            buildVerified: latest.buildVerified,
            snapshotId: latest.snapshotId,
          };
          if (signal.aborted) {
            throw new DOMException('Snapshot restore aborted', 'AbortError');
          }
          const res = await fetch(latest.downloadUrl, { signal });
          if (!res.ok) {
            throw new Error(`Snapshot download failed: ${res.status}`);
          }
          const downloaded = await res.blob();
          if (downloaded.size === 0) {
            throw new Error('Empty snapshot');
          }
          return downloaded;
        });
        const wc = webcontainerRef.current;
        if (!wc) {
          setSnapshotBootstrap('none');
          return;
        }
        await waitForTemplateReady();
        if (signal.aborted) {
          throw new DOMException('Snapshot restore aborted', 'AbortError');
        }
        await applySnapshotZipToWebContainer(wc, blob, signal);
        restartDevServerAfterSnapshot(wc);
        markSnapshotPreviewReady({
          buildVerified: latestMeta.buildVerified === true,
          knownBuildSuccess: latestMeta.buildVerified === true,
        });
        if (latestMeta.llmInteractionId) {
          setActiveSnapshotInteractionId(latestMeta.llmInteractionId);
          recordSnapshotFingerprintForInteraction(
            latestMeta.llmInteractionId,
            latestMeta.buildVerified === true,
          );
        }
        if (latestMeta.snapshotId) {
          setActiveSnapshotId(latestMeta.snapshotId);
        }
        setSnapshotBootstrap('restored');
        onSnapshotRestored?.(latestMeta);
      } catch (e) {
        // Superseded by a newer bootstrap restore or an explicit generation-pill click —
        // that call owns the outcome now; applying anything here would be a stale write.
        if (signal.aborted) return;
        if (isBenignMissingSnapshotError(e)) {
          setSnapshotRestoreError(null);
          setSnapshotBootstrap('none');
          const wc = webcontainerRef.current ?? getWebContainer();
          if (wc) {
            await waitForTemplateReady();
            await resetContainerToTemplate(wc);
            restartDevServerAfterSnapshot(wc);
          }
          setActiveSnapshotInteractionId(null);
          setActiveSnapshotId(null);
          return;
        }
        const message =
          e instanceof Error ? e.message : 'Failed to restore prototype snapshot';
        console.warn('[Workbench] Snapshot restore failed', e);
        setSnapshotRestoreError(message);
        setSnapshotBootstrap('failed');
      }
    });
  }, [
    bootStatus,
    projectId,
    conversationId,
    onSnapshotRestored,
    bootstrapRetryNonce,
    snapshotIndexLoading,
    recordSnapshotFingerprintForInteraction,
  ]);

  /** Flush pending file-edit snapshot on tab hide / unload (best-effort). */
  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const flushPendingSnapshot = (): void => {
      if (fileSnapshotDebounceRef.current !== undefined) {
        clearTimeout(fileSnapshotDebounceRef.current);
        fileSnapshotDebounceRef.current = undefined;
      }
      void runAutoPrototypeSnapshot('files');
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        flushPendingSnapshot();
      }
    };

    window.addEventListener('pagehide', flushPendingSnapshot);
    window.addEventListener('beforeunload', flushPendingSnapshot);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flushPendingSnapshot);
      window.removeEventListener('beforeunload', flushPendingSnapshot);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [projectId, conversationId, runAutoPrototypeSnapshot]);

  /**
   * Chrome (and other browsers) freeze/throttle a long-backgrounded tab's timers, network, and
   * nested workers — the WebContainer's dev server runs inside exactly such a worker, so a long
   * time away can leave it genuinely unresponsive with no error until the user notices the dead
   * preview and clicks Restart. This proactively re-checks health on tab *return* and recovers
   * without waiting for that manual step. Scoped to `devServerStatus` only — a stuck `bootStatus`
   * already self-resolves via `WEBCONTAINER_BOOT_TIMEOUT_MS` regardless of tab visibility.
   */
  useEffect(() => {
    if (!projectId || !conversationId) return undefined;

    const HIDDEN_RECOVERY_THRESHOLD_MS = 60_000;
    const hiddenAtRef = { current: null as number | null };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const hiddenAt = hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (hiddenAt == null || Date.now() - hiddenAt < HIDDEN_RECOVERY_THRESHOLD_MS) return;

      const status = workbenchStore.devServerStatus.get();
      if (status !== 'crashed' && status !== 'error') return;
      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      if (!wc) return;
      restartDevServerAfterSnapshot(wc);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [projectId, conversationId]);

  /** Cross-origin isolation is required independently of deferred boot timing. */
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.crossOriginIsolated) {
      workbenchStore.bootStatus.set('error');
      workbenchStore.bootError.set(
        'Cross-origin isolation is required. Use a browser with COOP/COEP headers enabled.',
      );
    }
  }, []);

  /** WebContainer lifecycle: optionally deferred until Nest-scoped snapshots or prototype UI loads. */
  useEffect(() => {
    if (typeof window === 'undefined' || !window.crossOriginIsolated) return;

    let mounted = true;

    const linkSingletonIntoStoreIfAlive = (): boolean => {
      if (!mounted) return false;
      const wc = getWebContainer();
      if (!wc) return false;
      syncWorkbenchStoreWithWebContainerPersist();
      webcontainerRef.current = wc;
      workbenchStore.webcontainer.set(wc);
      workbenchStore.bootStatus.set('ready');
      workbenchStore.bootError.set(undefined);
      return true;
    };

    if (!shouldBootWebContainer) {
      if (!linkSingletonIntoStoreIfAlive()) {
        workbenchStore.bootStatus.set('idle');
        workbenchStore.bootError.set(undefined);
      }
      return () => {
        mounted = false;
        webcontainerRef.current = null;
        workbenchStore.reset();
      };
    }

    workbenchStore.bootStatus.set('booting');
    workbenchStore.bootError.set(undefined);

    if (linkSingletonIntoStoreIfAlive()) {
      return () => {
        mounted = false;
        webcontainerRef.current = null;
        workbenchStore.reset();
      };
    }

    void bootWebContainer()
      .then((wc) => {
        if (!mounted) return;
        syncWorkbenchStoreWithWebContainerPersist();
        webcontainerRef.current = wc;
        workbenchStore.webcontainer.set(wc);
        workbenchStore.bootStatus.set('ready');
        workbenchStore.bootError.set(undefined);
      })
      .catch((err: unknown) => {
        console.error('[Workbench] WebContainer boot failed:', err);
        if (!mounted) return;
        workbenchStore.bootStatus.set('error');
        let msg = err instanceof Error ? err.message : 'WebContainer failed to start';
        if (/single WebContainer|Only a single/i.test(msg)) {
          msg =
            'Only one WebContainer instance is allowed per tab. Refresh this page (F5), especially after a hot reload, or close other tabs running the workbench.';
        }
        workbenchStore.bootError.set(msg);
      });

    return () => {
      mounted = false;
      webcontainerRef.current = null;
      workbenchStore.reset();
      // Do not teardown WebContainer here: React Strict Mode remounts before async
      // teardown finishes and @webcontainer/api allows only one boot per page.
    };
  }, [shouldBootWebContainer]);

  const apiUrl =
    projectId && conversationId
      ? ApiUrls.projectConversationBuildStream(projectId, conversationId)
      : '';

  /**
   * Must stay stable while `apiUrl` switches tmp → project after `projectCreated`.
   * `useChat` recreates the Chat when `id` changes; a new instance has empty `messages`, so
   * `addToolOutput` (bound via ref to the new chat) runs against [] and throws on `.parts`.
   */
  const workbenchChatId = useMemo(() => {
    const turnId =
      streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId ?? 'pending';
    return `workbench-build:${conversationId ?? 'pending'}:${turnId}`;
  }, [conversationId, streamingLlmInteractionId]);

  useEffect(() => {
    chefProjectIdRef.current = null;
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      setToolSteps([]);
      return;
    }
    try {
      const raw = sessionStorage.getItem(workbenchBrowserPersistKey(workbenchChatId));
      if (!raw) {
        setToolSteps([]);
        return;
      }
      const data = JSON.parse(raw) as {
        toolSteps?: ToolStep[];
        chefProjectId?: string | null;
      };
      if (Array.isArray(data.toolSteps)) {
        setToolSteps(data.toolSteps);
      } else {
        setToolSteps([]);
      }
      if (typeof data.chefProjectId === 'string' && data.chefProjectId.trim()) {
        chefProjectIdRef.current = data.chefProjectId.trim();
      }
    } catch {
      setToolSteps([]);
    }
  }, [workbenchChatId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return;
    }
    if (toolStepsPersistTimerRef.current !== null) {
      clearTimeout(toolStepsPersistTimerRef.current);
    }
    toolStepsPersistTimerRef.current = setTimeout(() => {
      toolStepsPersistTimerRef.current = null;
      try {
        sessionStorage.setItem(
          workbenchBrowserPersistKey(workbenchChatId),
          JSON.stringify({
            v: 1,
            toolSteps,
            chefProjectId: chefProjectIdRef.current,
          }),
        );
      } catch {
        /* ignore quota */
      }
    }, TOOL_STEPS_PERSIST_DEBOUNCE_MS);
    return () => {
      if (toolStepsPersistTimerRef.current !== null) {
        clearTimeout(toolStepsPersistTimerRef.current);
        toolStepsPersistTimerRef.current = null;
      }
    };
  }, [toolSteps, workbenchChatId, persistVersion]);

  useEffect(() => {
    if (!buildTimeline) return;
    const id = streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId;
    if (!id) return;
    if (buildSessionPersistTimerRef.current !== null) {
      clearTimeout(buildSessionPersistTimerRef.current);
    }
    buildSessionPersistTimerRef.current = setTimeout(() => {
      buildSessionPersistTimerRef.current = null;
      void patchBuildSessionApi({
        buildTimeline: buildTimeline as unknown as Record<string, unknown>,
      });
    }, BUILD_SESSION_PATCH_DEBOUNCE_MS);
    return () => {
      if (buildSessionPersistTimerRef.current !== null) {
        clearTimeout(buildSessionPersistTimerRef.current);
        buildSessionPersistTimerRef.current = null;
      }
    };
  }, [buildTimeline, patchBuildSessionApi, streamingLlmInteractionId]);

  /**
   * `DefaultChatTransport` POSTs JSON that includes AI SDK transcript `messages`; ideoz-ai-api
   * merges Flask-built prototyper context into the first user turn before forwarding to Chef.
   */
  const transport = useMemo(() => {
    const placeholder =
      typeof window !== 'undefined'
        ? `${window.location.origin}/__workbench_build_stream_pending__`
        : '/__workbench_build_stream_pending__';
    return new DefaultChatTransport({
      api: apiUrl || placeholder,
      headers: () => ({
        Authorization: `Bearer ${accessTokenRef.current}`,
      }),
      body: () => {
        const manual = collectWorkbenchManualFilesForRequest();
        const manualDiffs = collectWorkbenchManualFileDiffsForRequest();
        const manualHashes = collectWorkbenchManualFileHashesForRequest(manual);
        const base: Record<string, unknown> = {
          modelProvider: modelRef.current.modelProvider,
          modelChoice: modelRef.current.modelChoice,
          ...(chefProjectIdRef.current ? { projectId: chefProjectIdRef.current } : {}),
          ...(streamingBuildLlmInteractionIdRef?.current
            ? { llmInteractionId: streamingBuildLlmInteractionIdRef.current }
            : {}),
          ...(manual ? { workbenchManualFiles: manual } : {}),
          ...(manualDiffs ? { workbenchManualFileDiffs: manualDiffs } : {}),
          ...(manualHashes ? { workbenchManualFileHashes: manualHashes } : {}),
        };
        return base;
      },
    });
  }, [apiUrl]);

  const applyAnnotationChunk = useCallback(
    (raw: unknown) => {
      const chunks = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
      for (const item of chunks) {
        if (!item || typeof item !== 'object') continue;
        const c = item as Record<string, unknown>;
        if (c.type === 'assetFiles' && Array.isArray(c.files)) {
          for (const item of c.files) {
            if (!item || typeof item !== 'object') continue;
            const { path, base64, mimeType } = item as {
              path?: string;
              base64?: string;
              mimeType?: string;
            };
            if (typeof path !== 'string' || typeof base64 !== 'string') continue;
            if (writtenAssetPathsRef.current.has(path)) continue;
            writtenAssetPathsRef.current.add(path);

            {
              const capturedTurn = turnSessionRef.current.generation;
              assetWriteChainRef.current = assetWriteChainRef.current
                .then(async () => {
                  if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                  const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
                  if (!wc) return;
                  await persistAssetFile(wc, path, base64, mimeType);
                })
                .catch((err) => {
                  console.warn('[Workbench] assetFiles write failed', path, err);
                });
            }
          }
          continue;
        }

        const chunkKey = JSON.stringify(c);
        if (processedAnnotationsRef.current.has(chunkKey)) continue;
        processedAnnotationsRef.current.add(chunkKey);

        if (c.type === 'demoLinkCreated') {
          const rawId = c.prototyperDemoLinkId;
          const id = typeof rawId === 'number' ? rawId : Number(rawId);
          if (Number.isFinite(id)) {
            onStreamDemoLinkId?.(id);
            const lid = streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId;
            if (lid) {
              onPatchMessageByLlmInteractionId?.(lid, { prototyper_demo_link_id: id });
            }
          }
        }

        if (c.type === 'brandLogo' && typeof c.src === 'string') {
          workbenchStore.brandLogoSrc.set(c.src);
        }

        if (c.type === 'projectCreated' && typeof c.projectId === 'string') {
          chefProjectIdRef.current = c.projectId;
          setPersistVersion((v) => v + 1);
          onProjectCreated?.(c.projectId);
        }

        if (c.type === 'postBuildSpecVerification') {
          // Advisory only — do not surface in the timeline once files were generated.
          continue;
        }

        if (c.type === 'planComplete' && c.plan && typeof c.plan === 'object') {
          const plan = c.plan as Record<string, unknown>;
          editPlannedFilePathsRef.current = plannedPathsFromPlanComplete(plan);
          const coverageOk = plan.coverageOk === true;
          const rawGaps = plan.coverageGaps;
          const gaps = Array.isArray(rawGaps) ? rawGaps : [];
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const next = applyPlanCompleteToTimeline(
            base,
            plan,
            coverageOk,
            gaps,
          );
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        if (c.type === 'fileRead' && typeof c.path === 'string') {
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const lineCount = typeof c.lineCount === 'number' ? c.lineCount : undefined;
          const key =
            typeof c.toolCallId === 'string' ? `read-${c.toolCallId}` : `read-${c.path}-${Date.now()}`;
          const next = applyExploreEventToTimeline(base, {
            key,
            title: `Read ${c.path}`,
            detail: lineCount !== undefined ? `${lineCount} lines` : undefined,
            status: 'done',
            toolName: 'readFile',
          });
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        if (c.type === 'fileSearch' && typeof c.query === 'string') {
          const base = buildTimelineRef.current ?? createEmptyTimeline();
          const hitCount = typeof c.hitCount === 'number' ? c.hitCount : 0;
          const key =
            typeof c.toolCallId === 'string'
              ? `search-${c.toolCallId}`
              : `search-${c.query}-${Date.now()}`;
          const pathGlob = typeof c.pathGlob === 'string' ? c.pathGlob : undefined;
          const next = applyExploreEventToTimeline(base, {
            key,
            title: `Search "${c.query}"`,
            detail:
              hitCount > 0
                ? `${hitCount} hit(s)${pathGlob ? ` in ${pathGlob}` : ''}`
                : `No matches${pathGlob ? ` in ${pathGlob}` : ''}`,
            status: 'done',
            toolName: 'searchFiles',
          });
          timelineAnnotationRef.current = next;
          buildTimelineRef.current = next;
          setBuildTimeline(next);
        }

        // Seed the WebContainer with the last saved project files at the start of an
        // edit turn. WebContainer is empty on a fresh browser session even though the
        // project exists in the DB — without seeding, searchReplace calls find nothing.
        if (c.type === 'pruneWorkspace' && Array.isArray(c.keep)) {
          const keep = c.keep.filter((p): p is string => typeof p === 'string');
          {
            const capturedTurn = turnSessionRef.current.generation;
            assetWriteChainRef.current = assetWriteChainRef.current
              .then(async () => {
                if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
                if (!wc) return;
                await executePrune(wc, keep);
              })
              .catch((err) => {
                console.warn('[Workbench] pruneWorkspace failed', err);
              });
          }
        }

        if (c.type === 'seedFiles' && c.files && typeof c.files === 'object') {
          const seedMap = c.files as Record<string, string>;
          {
            const capturedTurn = turnSessionRef.current.generation;
            assetWriteChainRef.current = assetWriteChainRef.current
              .then(async () => {
                if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
                if (!wc) return;

                for (const [filePath, content] of Object.entries(seedMap)) {
                  if (isTurnStale(turnSessionRef.current, capturedTurn)) return;
                  if (typeof content !== 'string') continue;
                  const existing = workbenchStore.files.get()[filePath];
                  if (existing?.modified) continue;
                  await persistUserFile(wc, filePath, content, false);
                }
              })
              .catch((err) => {
                console.warn('[Workbench] seedFiles write failed', err);
              });
          }
        }
      }
    },
    [onProjectCreated, onStreamDemoLinkId, onPatchMessageByLlmInteractionId, streamingLlmInteractionId],
  );

  const onDataPart = useCallback(
    (part: unknown) => {
      if (!part || typeof part !== 'object') return;
      const p = part as Record<string, unknown>;
      const t = p.type;
      if (t === 'data-prototyperUsage') {
        const usage = Array.isArray(p.data) ? p.data[0] : undefined;
        const finishReason =
          usage && typeof usage === 'object'
            ? (usage as Record<string, unknown>).finishReason
            : undefined;
        streamTruncatedRef.current = finishReason === 'length';
        onCreditsRefresh?.();
        return;
      }
      if (t === 'data-prototyperAnnotation') {
        applyAnnotationChunk(p.data);
      }
      if (typeof t === 'string' && t.startsWith('data-') && 'data' in p) {
        const data = p.data;
        if (Array.isArray(data) || (data && typeof data === 'object')) {
          applyAnnotationChunk(data);
        }
      }
    },
    [applyAnnotationChunk, onCreditsRefresh],
  );

  const { messages, sendMessage, addToolOutput, stop, status } = useChat({
    id: workbenchChatId,
    transport,
    /**
     * Disabled: the SDK can fire multiple continuation paths. We continue explicitly in
     * `scheduleContinueAfterClientTools` after each client tool returns, when the assistant
     * stream is idle (`chatStatusRef` not streaming/submitted) so we do not POST before
     * more tool parts arrive.
     */
    sendAutomaticallyWhen: () => false,
    onToolCall: async ({ toolCall }) => {
      await assetWriteChainRef.current;

      // readFile/searchFiles execute on the server — skip client handling entirely.
      if (isServerExecutedPrototyperTool(toolCall.toolName)) {
        return;
      }

      if (turnSessionRef.current.aborted) {
        // The turn this call belongs to was explicitly aborted (conversation switch or
        // stream cancel) — ignore it rather than mutate a now-unrelated chat's WebContainer.
        // Belt-and-suspenders alongside the stream-abort effect below: closes the small
        // window between the underlying fetch being aborted and this callback actually
        // stopping being invoked.
        return;
      }

      const toolName = toolCall.toolName;
      const toolCallId = toolCall.toolCallId;
      const capturedTurn = turnSessionRef.current.generation;
      const turnSignal = turnAbortControllerRef.current.signal;
      toolTurnGenerationRef.current = capturedTurn;
      clientToolsInFlightRef.current += 1;
      workbenchStore.clientToolsBusy.set(true);

      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      const input = getToolInput(toolCall);

      const stepKey = `${toolCallId}`;
      setToolSteps((prev) => {
        const next = capToolSteps([
          ...prev,
          { key: stepKey, toolName, args: input, isComplete: false },
        ]);
        toolStepsRef.current = next;
        return next;
      });

      const completeToolStep = (result: string, isError = false): void => {
        clientToolsInFlightRef.current = Math.max(0, clientToolsInFlightRef.current - 1);
        if (clientToolsInFlightRef.current === 0) {
          workbenchStore.clientToolsBusy.set(false);
        }
        if (isTurnStale(turnSessionRef.current, capturedTurn)) {
          return;
        }
        toolResultContinuationPendingRef.current = true;
        setToolSteps((prev) => {
          const next = capToolSteps(
            prev.map((s) =>
              s.key === stepKey ? { ...s, result, isComplete: true } : s,
            ),
          );
          toolStepsRef.current = next;
          return next;
        });
        if (isError) {
          deferAddToolOutput(
            {
              tool: toolName,
              toolCallId,
              state: 'output-error',
              errorText: result,
            },
            capturedTurn,
          );
        } else {
          deferAddToolOutput({ tool: toolName, toolCallId, output: result }, capturedTurn);
        }
      };

      if (!wc) {
        completeToolStep('WebContainer not ready', true);
        return;
      }

      try {
        let output: string;

        if (toolName === 'writeFile') {
          const path = String(input.path ?? '');
          const content = String(input.content ?? '');
          output = await executeWriteFile(wc, path, content, turnSignal);
        } else if (toolName === 'searchReplace') {
          output = await executeSearchReplace(
            wc,
            String(input.path ?? ''),
            String(input.oldString ?? ''),
            String(input.newString ?? ''),
            input.replaceAll === true,
            turnSignal,
          );
        } else if (toolName === 'deleteFile') {
          output = await executeDeleteFile(wc, String(input.path ?? ''), turnSignal);
        } else if (toolName === 'build') {
          if (turnSignal.aborted) {
            completeToolStep('Skipped: turn was cancelled before the build could start.', true);
            return;
          }

          // Chef has finished writing every file for this turn — persist them now, keyed to
          // this turn, *before* the (30-90s) build, so a refresh/lost-tab mid-build no longer
          // loses the turn. Concurrent, never awaited: it must not delay or be able to break
          // the build (see `force` on runAutoPrototypeSnapshot for why it isn't dropped by the
          // isStreaming/clientToolsBusy guard that would otherwise silently swallow it here).
          const preBuildLid = streamingBuildLlmInteractionIdRef?.current ?? streamingLlmInteractionId;
          if (preBuildLid) {
            void runAutoPrototypeSnapshot('build-start', {
              buildVerified: false,
              force: true,
              llmInteractionId: preBuildLid,
            }).catch((err) => {
              console.warn('[Workbench] pre-build snapshot failed', err);
            });
          }

          workbenchStore.buildStatus.set('building');
          try {
            const result = await executeBuild(wc, undefined, turnSignal);
            if (isTurnStale(turnSessionRef.current, capturedTurn)) {
              // A stale turn's build result must not flip the *current* conversation's
              // buildStatus/repeat-error counters — those belong to whatever turn is active now.
              output = result.formattedOutput;
              completeToolStep(output, !result.ok);
              return;
            }
            workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
            if (result.ok) workbenchStore.hasUnbuiltEdits.set(false);

            let buildOutput = result.formattedOutput;
            if (!result.ok) {
              const rep = buildRepeatRef.current;
              if (result.errorSig === rep.lastSig) {
                rep.count++;
              } else {
                rep.lastSig = result.errorSig;
                rep.count = 1;
              }
              if (rep.count >= MAX_BUILD_REPEAT_ERRORS) {
                const hasDirtyManualDrafts =
                  Object.keys(workbenchStore.getModifiedFiles()).length > 0;
                buildOutput += hasDirtyManualDrafts
                  ? `\n\nSTOP retrying — the same error has occurred ${rep.count} times. ` +
                    `Prefer minimal surgical edits; do NOT wholesale-rewrite paths the user edited in IDE unless unavoidable.`
                  : `\n\nSTOP retrying — the same error has occurred ${rep.count} times. ` +
                    `Rewrite the affected file(s) from scratch instead of patching.`;
              }
            } else {
              buildRepeatRef.current = { lastSig: '', count: 0 };
              queueMicrotask(() => {
                void runAutoPrototypeSnapshot('build', {
                  buildVerified: true,
                  force: true,
                  llmInteractionId: preBuildLid,
                });
              });
            }
            output = buildOutput;
          } finally {
            // Never touch buildStatus on behalf of a turn that's since been superseded —
            // it may now belong to a different, currently-active conversation.
            if (
              !isTurnStale(turnSessionRef.current, capturedTurn) &&
              workbenchStore.buildStatus.get() === 'building'
            ) {
              workbenchStore.buildStatus.set('error');
            }
          }
        } else if (toolName === 'configureSession') {
          const mode = String(input.mode ?? 'website');
          const skills = Array.isArray(input.skills)
            ? (input.skills as string[])
            : undefined;
          output = executeConfigureSession({ mode, skills });
        } else {
          completeToolStep(`Unknown tool: ${toolName}`, true);
          return;
        }

        completeToolStep(output);
      } catch (e) {
        completeToolStep(e instanceof Error ? e.message : 'Tool execution failed', true);
      }
    },
    onData: (dataPart) => {
      onDataPart(dataPart);
    },
    onError: () => {
      if (
        buildRepeatRef.current.count < MAX_BUILD_REPEAT_ERRORS &&
        shouldAutoContinueAfterFailedBuild(messagesRef.current, toolStepsRef.current)
      ) {
        requestStreamContinuation();
      }
    },
    onFinish: async ({
      messages: finishMessages,
      isAbort,
      isError,
      isDisconnect,
    }) => {
      messagesRef.current = finishMessages;
      onCreditsRefresh?.();

      const lid = streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId;
      const transportFailed = Boolean(isAbort || isError || isDisconnect);
      const toolBuildFailed = lastToolStepBuildFailed(toolStepsRef.current);
      let buildOk =
        workbenchStore.buildStatus.get() === 'success' && !toolBuildFailed;

      // Auto-continue after a failed build/type-check (unless the user aborted).
      // Allow retry even when the last POST errored (e.g. Chef 409 while prior stream
      // was still persisting) so repair is not stuck after a transport failure.
      if (!isAbort && tryAutoContinueAfterFailedBuild(finishMessages)) {
        return;
      }

      // Resume a turn that hit the output-token ceiling before it could call `build`.
      if (!transportFailed && !buildOk && tryAutoContinueAfterTruncation()) {
        return;
      }

      // Model wrote files but never called build — compile once client-side (last resort only).
      if (!transportFailed && !buildOk) {
        const completenessInput = getEditLoopCompletenessInput(finishMessages);
        if (allowOpportunisticBuild(completenessInput)) {
          const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
          if (wc) {
            workbenchStore.buildStatus.set('building');
            try {
              const result = await executeBuild(wc);
              workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
              if (result.ok) {
                workbenchStore.hasUnbuiltEdits.set(false);
                buildOk = true;
                buildRepeatRef.current = { lastSig: '', count: 0 };
                queueMicrotask(() => {
                  void runAutoPrototypeSnapshot('build', {
                    buildVerified: true,
                    force: true,
                    llmInteractionId: lid,
                  });
                });
              }
            } catch {
              workbenchStore.buildStatus.set('error');
            }
          }
        }
      }

      // Planned edit work or build still open — keep the multi-round client-tool loop alive.
      if (
        !transportFailed &&
        !buildOk &&
        isEditTurnUnfinished(getEditLoopCompletenessInput(finishMessages))
      ) {
        requestStreamContinuation();
        return;
      }

      const turnSucceeded = !transportFailed && buildOk;

      if (lid) {
        // Recompute from `toolStepsRef.current` rather than trusting `buildTimelineRef.current`:
        // the displayed timeline is normally rebuilt by a `requestAnimationFrame`-scheduled
        // effect (below) that watches `toolSteps`, so it can still be a frame or two behind the
        // very last tool call (e.g. `build`) when this stream's `onFinish` fires synchronously —
        // and once finalized here, `patchMessageByLlmInteractionId`/`patchBuildSessionApi` freeze
        // this as the permanent, non-live version of the timeline. Without this, the persisted
        // timeline can be missing whichever "writing files" / "build" steps hadn't made it
        // through that RAF yet, and there is no later live render to backfill them.
        const freshSteps = toolStepsRef.current.map((s) => ({
          key: s.key,
          toolName: s.toolName,
          isComplete: s.isComplete,
          args: s.args,
          result: s.result,
        }));
        const freshBase = timelineFromToolSteps(
          freshSteps,
          freshSteps.length > 0 ? undefined : messagesRef.current,
        );
        const freshMerged = mergeTimelinePreservingAnnotations(
          freshBase,
          timelineAnnotationRef.current,
        );
        const base = freshMerged.segments.length
          ? freshMerged
          : (buildTimelineRef.current ?? createEmptyTimeline());
        let finalT: BuildTimelineV2;
        let sessionStatus: string;
        if (transportFailed) {
          finalT = markStreamError(base);
          sessionStatus = 'error';
        } else if (!buildOk) {
          finalT = markStreamEndedWithoutSuccessfulBuild(base);
          sessionStatus = 'error';
        } else {
          finalT = markStreamSuccess(base);
          sessionStatus = 'success';
        }
        buildTimelineRef.current = finalT;
        setBuildTimeline(finalT);
        const completedAt = new Date().toISOString();
        void patchBuildSessionApi({
          buildTimeline: finalT as unknown as Record<string, unknown>,
          buildStatus: sessionStatus,
          buildCompletedAt: completedAt,
        });
        onPatchMessageByLlmInteractionId?.(lid, {
          buildTimeline: finalT,
          buildStatus: sessionStatus,
          prototypeDisplayName:
            getPrototypeDisplayName?.() ?? buildSessionPrototypeMetaRef.current.prototypeDisplayName,
          prototypeVersionIndex: buildSessionPrototypeMetaRef.current.prototypeVersionIndex ?? undefined,
          // Never show the "Ideoz AI has completed creating the prototyper
          // successfully" banner — the message text + the prototype pill already
          // convey completion. (Error banners are handled separately in
          // ConversationModule via `prototypeError`.)
          hasBanner: false,
          bannerType: undefined,
          drawType: undefined,
          bannerPosition: undefined,
        });
        if (turnSucceeded) {
          // Turn is only marked "built" once the snapshot is confirmed persisted —
          // see the `onBuildStreamTurnCompleted` call inside `runAutoPrototypeSnapshot`.
          setActiveSnapshotInteractionId(lid);
        }
      }

      if (isAbort || isError || isDisconnect) {
        if (!suppressStreamAbortRef.current) {
          abortCurrentTurn();
          clearDeferToolOutputTimeouts();
        }
        workbenchStore.isStreaming.set(false);
        return;
      }
      if (turnSucceeded) {
        cancelStreamContinuationRef.current?.();
        cancelStreamContinuationRef.current = null;
        toolResultContinuationPendingRef.current = false;
        streamTruncatedRef.current = false;
        truncationContinueRef.current = 0;
        void runAutoPrototypeSnapshot('stream-end', {
          buildVerified: true,
          force: true,
          llmInteractionId: lid,
        });
      }
      const clearIfNoPendingTools = async (msgs: readonly unknown[]): Promise<void> => {
        if (lastAssistantHasPendingClientTools(msgs)) return;
        const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
        if (wc) {
          try {
            await flushDirtyEditorFilesBeforeStream(wc);
          } catch (err) {
            console.warn('[Workbench] post-stream manual edit flush failed', err);
          }
        }
        workbenchStore.isStreaming.set(false);
        workbenchStore.clearManualEditConsumedFlags();
      };
      void clearIfNoPendingTools(finishMessages);
      if (lastAssistantHasPendingClientTools(finishMessages)) {
        queueMicrotask(() => {
          void clearIfNoPendingTools(messagesRef.current);
          if (lastAssistantHasPendingClientTools(messagesRef.current)) {
            requestAnimationFrame(() => {
              void clearIfNoPendingTools(messagesRef.current);
            });
          }
        });
      }
    },
  });

  chatStatusRef.current = status;

  addToolOutputRef.current = addToolOutput;
  sendMessageRef.current = sendMessage as unknown as (msg?: unknown) => void;

  useEffect(() => {
    toolStepsRef.current = toolSteps;
  }, [toolSteps]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (timelineRebuildRafRef.current !== null) {
      cancelAnimationFrame(timelineRebuildRafRef.current);
    }
    timelineRebuildRafRef.current = requestAnimationFrame(() => {
      timelineRebuildRafRef.current = null;
      const steps = toolSteps.map((s) => ({
        key: s.key,
        toolName: s.toolName,
        isComplete: s.isComplete,
        args: s.args,
        result: s.result,
      }));
      const base = timelineFromToolSteps(steps, steps.length > 0 ? undefined : messages);
      const productionBuildOk =
        buildStatus === 'success' && !lastToolStepBuildFailed(steps);
      const merged = mergeTimelinePreservingAnnotations(base, timelineAnnotationRef.current);
      const t = mergeLiveTerminalSegments(merged, {
        productionBuildOk,
        finalizeComplete: false,
      });
      setBuildTimeline(t);
      buildTimelineRef.current = t;
    });
    return () => {
      if (timelineRebuildRafRef.current !== null) {
        cancelAnimationFrame(timelineRebuildRafRef.current);
        timelineRebuildRafRef.current = null;
      }
    };
  }, [toolSteps, messages, buildStatus]);

  const startGeneration = useCallback(
    (prompt: string) => {
      if (!apiUrl) {
        return;
      }
      setWarmBootLatch((n) => n + 1);
      suppressStreamAbortRef.current = true;
      void stop();
      bumpTurnGeneration(turnSessionRef.current, false);
      resumeBuildAbortRef.current?.abort();
      resumeBuildAbortRef.current = null;
      clearDeferToolOutputTimeouts();

      const beginStream = (): void => {
        workbenchStore.isStreaming.set(true);
        workbenchStore.clientToolsBusy.set(false);
        workbenchStore.buildStatus.set('idle');
        recordProductionBuildOutcome(false);
        processedAnnotationsRef.current.clear();
        writtenAssetPathsRef.current.clear();
        assetWriteChainRef.current = Promise.resolve();
        buildRepeatRef.current = { lastSig: '', count: 0 };
        cancelStreamContinuationRef.current?.();
        cancelStreamContinuationRef.current = null;
        continuationSentRef.current = false;
        toolResultContinuationPendingRef.current = false;
        toolContinuationCountRef.current = 0;
        editPlannedFilePathsRef.current = [];
        streamTruncatedRef.current = false;
        truncationContinueRef.current = 0;
        timelineAnnotationRef.current = null;
        // A prior turn's "stream stalled" banner must not bleed into a fresh generation —
        // otherwise it sits stale over an otherwise-healthy new build until the user refreshes.
        setWorkbenchPersistError(null);
        workbenchStore.snapshotPreviewReady.set(false);
        // A follow-up generation in the same conversation must not inherit the *previous*
        // turn's "preview is visible" state — otherwise View Insights / Publish stay revealed
        // (stale-true) through the whole new build, since nothing else resets this atom until
        // `Workbench`'s own effect re-evaluates it, which only happens once its component tree
        // is actually re-rendering against the new build's state.
        workbenchStore.previewReady.set(false);

        const empty = createEmptyTimeline();
        setBuildTimeline(empty);
        buildTimelineRef.current = empty;

        const lid = streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId;
        if (lid) {
          const displayName = getPrototypeDisplayName?.();
          buildSessionPrototypeMetaRef.current = {
            prototypeDisplayName: displayName ?? null,
            prototypeVersionIndex: undefined,
          };
          void (async () => {
            try {
              const body = {
                prototypeDisplayName: displayName,
                buildTimeline: empty as unknown as Record<string, unknown>,
              };
              const created = await prototyperService.createPrototyperBuildSession(lid, body);
              const row = created as {
                prototypeVersionIndex?: number | null;
                prototypeDisplayName?: string | null;
              };
              buildSessionPrototypeMetaRef.current.prototypeVersionIndex =
                row.prototypeVersionIndex ?? null;
              if (row.prototypeDisplayName != null) {
                buildSessionPrototypeMetaRef.current.prototypeDisplayName = row.prototypeDisplayName;
              }
            } catch (e) {
              console.warn('[Workbench] create prototyper build session failed', e);
            }
          })();
        }

        void sendMessageRef.current?.({ text: prompt });
        suppressStreamAbortRef.current = false;
      };

      const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
      if (wc && Object.keys(workbenchStore.getModifiedFiles()).length > 0) {
        void flushDirtyEditorFilesBeforeStream(wc)
          .catch((err) => {
            console.warn('[Workbench] pre-stream manual edit flush failed', err);
          })
          .finally(beginStream);
        return;
      }

      beginStream();
    },
    [
      apiUrl,
      getPrototypeDisplayName,
      projectId,
      streamingLlmInteractionId,
      stop,
      clearDeferToolOutputTimeouts,
    ],
  );

  const restorePrototypeSnapshotForInteraction = useCallback(
    async (llmInteractionId: string, hint?: PrototypeSnapshotRestoreHint) => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!pid || !cid) return;
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return;

      if (
        activeSnapshotInteractionIdRef.current === llmInteractionId &&
        activeSnapshotIdRef.current
      ) {
        return;
      }

      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) {
        throw new Error('Prototype is still building. Wait for the current build to finish.');
      }

      setWarmBootLatch((n) => n + 1);
      setRestoreInteractionLoadingId(llmInteractionId);

      try {
        return await withRestoreMutex(restoreMutexRef.current, llmInteractionId, async (signal) => {
          const wc = await waitForWebContainerReady(
            () => webcontainerRef.current ?? workbenchStore.webcontainer.get(),
          );
          if (!wc) {
            throw new Error('WebContainer did not become ready in time');
          }
          await waitForTemplateReady();
          const latest = await prototyperService.getPrototypeSnapshotByInteraction(
            pid,
            cid,
            llmInteractionId,
          );
          if (signal.aborted) {
            throw new DOMException('Restore aborted', 'AbortError');
          }
          const res = await fetch(latest.downloadUrl, { signal });
          if (!res.ok) {
            throw new Error(`Snapshot download failed (${res.status})`);
          }
          const blob = await res.blob();
          await applySnapshotZipToWebContainer(wc, blob, signal);
          restartDevServerAfterSnapshot(wc);
          const knownBuildSuccess =
            latest.buildVerified === true ||
            hint?.buildStatus === 'success' ||
            hint?.hasBuildTimeline === true;
          markSnapshotPreviewReady({
            buildVerified: latest.buildVerified === true,
            knownBuildSuccess,
          });
          setActiveSnapshotInteractionId(llmInteractionId);
          setActiveSnapshotId(latest.snapshotId);
          // Take ownership of `snapshotBootstrap` too. Starting this restore aborts any in-flight
          // boot-time bootstrap, and that one bails on `signal.aborted` *without* settling — on the
          // assumption the superseding call owns the outcome. It didn't, so the state stayed
          // 'pending' forever with nothing left to move it.
          setSnapshotBootstrap('restored');
          recordSnapshotFingerprintForInteraction(llmInteractionId, latest.buildVerified === true);
          return latest;
        });
      } finally {
        setRestoreInteractionLoadingId((current) =>
          current === llmInteractionId ? null : current,
        );
      }
    },
    [recordSnapshotFingerprintForInteraction],
  );

  /**
   * Finish a build that was interrupted (refresh / lost tab) after its files were snapshotted
   * at build-start but before the build was verified. Restores that turn's own files (NOT
   * whatever the boot-time bootstrap restored — it prefers the newest *verified* entry, which
   * may be an older turn), re-runs the build locally with no LLM stream, and uploads a verified
   * snapshot on success. Returns `'failed'` only for a genuine local build failure — callers
   * should fall back to a real LLM fix-stream only in that case, never for `'skipped'`.
   */
  const resumeInterruptedBuildImpl = useCallback(
    async (llmInteractionId: string): Promise<ResumeBuildOutcome> => {
      const { projectId: pid, conversationId: cid } = projectConversationRef.current;
      if (!pid || !cid) return 'skipped';
      if (typeof window === 'undefined' || !window.crossOriginIsolated) return 'skipped';
      if (resumeBuildInFlightRef.current) return 'skipped';
      if (resumeBuildAttemptedRef.current.has(llmInteractionId)) return 'skipped';
      if (workbenchStore.isStreaming.get() || workbenchStore.clientToolsBusy.get()) return 'skipped';

      try {
        const server = await prototyperService.getPrototypeSnapshotByInteraction(
          pid,
          cid,
          llmInteractionId,
        );
        if (server.buildVerified === true) {
          // Another tab (or an earlier attempt this session) already finished it.
          onBuildStreamTurnCompleted?.(llmInteractionId);
          return 'skipped';
        }
      } catch (e) {
        if (isBenignMissingSnapshotError(e)) return 'skipped';
        // Real network error checking status — fall through and try to resume anyway.
      }

      resumeBuildInFlightRef.current = llmInteractionId;
      resumeBuildAttemptedRef.current.add(llmInteractionId);
      const controller = new AbortController();
      resumeBuildAbortRef.current = controller;
      const watchdog = window.setTimeout(() => controller.abort(), RESUME_BUILD_MAX_MS);
      const capturedTurn = turnSessionRef.current.generation;
      const conversationAtStart = cid;

      // The WebContainer is a tab-wide singleton — a resume that survives a conversation
      // switch or a new stream starting would build the *new* conversation's files under the
      // *old* turn's id. Re-checked after every await below.
      const stillOurs = (): boolean =>
        !controller.signal.aborted &&
        projectConversationRef.current.conversationId === conversationAtStart &&
        !isTurnStale(turnSessionRef.current, capturedTurn) &&
        !workbenchStore.isStreaming.get();

      try {
        if (
          activeSnapshotInteractionIdRef.current !== llmInteractionId ||
          !activeSnapshotIdRef.current
        ) {
          // No hint — passing `hasBuildTimeline: true` would mark this unverified build as
          // known-successful and flip buildStatus to 'success' before we've rebuilt anything.
          await restorePrototypeSnapshotForInteraction(llmInteractionId);
        }
        if (!stillOurs()) return 'skipped';

        const wc = await waitForWebContainerReady(
          () => webcontainerRef.current ?? workbenchStore.webcontainer.get(),
        );
        if (!wc) return 'failed';
        await waitForTemplateReady();
        if (!stillOurs()) return 'skipped';

        workbenchStore.buildStatus.set('building');
        const result = await executeBuild(wc, undefined, controller.signal);
        if (!stillOurs()) return 'skipped';
        workbenchStore.buildStatus.set(result.ok ? 'success' : 'error');
        if (!result.ok) return 'failed';
        workbenchStore.hasUnbuiltEdits.set(false);

        await runAutoPrototypeSnapshot('resume-build', {
          buildVerified: true,
          force: true,
          llmInteractionId,
        });
        try {
          await prototyperService.patchPrototyperBuildSession(llmInteractionId, {
            buildStatus: 'success',
            buildCompletedAt: new Date().toISOString(),
          });
        } catch (e) {
          console.warn('[Workbench] resume build-session patch failed', e);
        }
        onPatchMessageByLlmInteractionId?.(llmInteractionId, { buildStatus: 'success' });
        onBuildStreamTurnCompleted?.(llmInteractionId);
        setActiveSnapshotInteractionId(llmInteractionId);
        return 'success';
      } catch (e) {
        if (e instanceof BuildAbortedError || controller.signal.aborted) return 'skipped';
        if (isSupersededRestoreError(e)) return 'skipped';
        console.warn('[Workbench] resumeInterruptedBuild failed', e);
        if (workbenchStore.buildStatus.get() === 'building') {
          workbenchStore.buildStatus.set('error');
        }
        return 'failed';
      } finally {
        window.clearTimeout(watchdog);
        if (resumeBuildAbortRef.current === controller) {
          resumeBuildAbortRef.current = null;
        }
        resumeBuildInFlightRef.current = null;
      }
    },
    [
      restorePrototypeSnapshotForInteraction,
      runAutoPrototypeSnapshot,
      onBuildStreamTurnCompleted,
      onPatchMessageByLlmInteractionId,
    ],
  );

  // Stable identity — `resumeInterruptedBuildImpl` changes whenever `runAutoPrototypeSnapshot`
  // does (e.g. on every `activeSnapshotInteractionId` change), but it lands in downstream
  // effect dependency arrays that shouldn't re-run just because that identity churned.
  const resumeInterruptedBuildRef = useRef(resumeInterruptedBuildImpl);
  useEffect(() => {
    resumeInterruptedBuildRef.current = resumeInterruptedBuildImpl;
  }, [resumeInterruptedBuildImpl]);
  const resumeInterruptedBuild = useCallback(
    (llmInteractionId: string): Promise<ResumeBuildOutcome> =>
      resumeInterruptedBuildRef.current(llmInteractionId),
    [],
  );

  const uploadVerifiedPrototypeSnapshot = useCallback(async (): Promise<string | undefined> => {
    const streamingTurnId =
      streamingBuildLlmInteractionIdRef.current ?? streamingLlmInteractionId ?? null;
    const isLiveWorkingCopy =
      !activeSnapshotInteractionId ||
      !streamingTurnId ||
      activeSnapshotInteractionId === streamingTurnId;

    if (!isLiveWorkingCopy && activeSnapshotId) {
      return activeSnapshotId;
    }

    const wc = webcontainerRef.current ?? workbenchStore.webcontainer.get();
    if (wc) {
      // Manual editor edits are written to the store immediately but only synced into the
      // WebContainer filesystem on a debounce (`EditorPanel`'s `handleChange`) — without this,
      // a snapshot forced right after a fast edit-then-publish click can be zipped from store
      // content the container never actually had loaded, silently diverging from build.ts's
      // own pre-build flush.
      await flushDirtyEditorFilesBeforeStream(wc).catch((err) => {
        console.warn('[Workbench] flush before publish snapshot failed', err);
      });
    }

    const finalized = await runAutoPrototypeSnapshot('build', { buildVerified: true, force: true });
    const snapshotId = finalized?.snapshotId ?? activeSnapshotId ?? undefined;
    if (!snapshotId) {
      // Nothing to hand the publish call — better to fail loudly here than let the server
      // silently fall back to whatever it considers the conversation's "latest ready"
      // snapshot, which may not be the one currently showing in the preview.
      throw new Error(
        'Could not prepare a build for publish. Try Rebuild, then publish again.',
      );
    }
    return snapshotId;
  }, [
    runAutoPrototypeSnapshot,
    activeSnapshotInteractionId,
    activeSnapshotId,
    streamingLlmInteractionId,
  ]);

  const retrySnapshotBootstrap = useCallback((): void => {
    setSnapshotRestoreError(null);
    setBootstrapRetryNonce((n) => n + 1);
    setSnapshotBootstrap('pending');
  }, []);

  const getSnapshotIndexEntry = useCallback((llmInteractionId: string) => {
    return snapshotIndexRef.current.get(llmInteractionId);
  }, []);

  /**
   * Genuinely abort the outgoing chat's stream before `workbenchChatId` moves on to a new
   * conversation or turn. `useChat`'s `id` change swaps to a brand-new `Chat` instance
   * synchronously during render (`@ai-sdk/react`'s internal `shouldRecreateChat`), and
   * `onToolCall`/`onData` are dispatched through a callbacks ref shared across instances —
   * so without this, an abandoned stream from the previous conversation/turn keeps running
   * and its tool calls/results land on whatever chat is now active. `stop` returned by
   * `useChat` is permanently bound to the instance it came from (an arrow-function class
   * field on `AbstractChat` that captures `this` at construction), so capturing it here and
   * calling it from this effect's cleanup — which fires for the OLD `workbenchChatId` right
   * before the new one's effect runs — reliably cancels the abandoned instance's fetch.
   */
  useEffect(() => {
    const stopOutgoing = stop;
    return () => {
      void stopOutgoing();
    };
  }, [stop]);

  const cancelWorkbenchStream = useCallback((): void => {
    abortCurrentTurn();
    resumeBuildAbortRef.current?.abort();
    resumeBuildAbortRef.current = null;
    clearDeferToolOutputTimeouts();
    cancelStreamContinuationRef.current?.();
    cancelStreamContinuationRef.current = null;
    stop();
    workbenchStore.isStreaming.set(false);
  }, [clearDeferToolOutputTimeouts, stop, abortCurrentTurn]);

  const isStreaming = useStore(workbenchStore.isStreaming);
  const previewUrl = useStore(workbenchStore.previewUrl);

  const isLoading = status === 'streaming' || status === 'submitted';

  return useMemo(
    () => ({
      messages,
      toolSteps,
      buildTimeline,
      isLoading,
      isStreaming,
      buildStatus,
      bootStatus,
      previewUrl,
      startGeneration,
      stop: cancelWorkbenchStream,
      snapshotBootstrap,
      snapshotRestoreError,
      restorePrototypeSnapshotForInteraction,
      uploadVerifiedPrototypeSnapshot,
      resumeInterruptedBuild,
      activeSnapshotInteractionId,
      restoreInteractionLoadingId,
      workbenchPersistError,
      snapshotIndexLoading,
      getSnapshotIndexEntry,
      retrySnapshotBootstrap,
    }),
    [
      messages,
      toolSteps,
      buildTimeline,
      isLoading,
      isStreaming,
      buildStatus,
      bootStatus,
      previewUrl,
      startGeneration,
      cancelWorkbenchStream,
      snapshotBootstrap,
      snapshotRestoreError,
      restorePrototypeSnapshotForInteraction,
      uploadVerifiedPrototypeSnapshot,
      resumeInterruptedBuild,
      activeSnapshotInteractionId,
      restoreInteractionLoadingId,
      workbenchPersistError,
      snapshotIndexLoading,
      getSnapshotIndexEntry,
      retrySnapshotBootstrap,
    ],
  );
}
